// Channel reliability, as the feed needs it: one map, handle -> rating.
//
// The rating is EDITORIAL — an operator's standing judgement of a channel,
// set in the admin coverage panel — and it deliberately feeds nothing but
// display: not extraction (a model told which channels the operator trusts
// starts agreeing with them) and not routing (membership must not depend on
// a setting that can change tomorrow). claim.ts turns it into visible words
// on each row; this module just owns the map.

import { get } from './api';

export type Reliability = 'high' | 'standard' | 'low';

interface ChannelMeta {
  reliability: Reliability | null;
  /** The operator's TREATMENT instruction — "often breaks news first,
   *  verify", "biased on strikes against X, solid on Z", "overstates
   *  interception success". Rendered as a tooltip wherever the channel is
   *  cited, so the tradecraft arrives at the moment of reading. */
  note: string | null;
}

let byHandle = new Map<string, ChannelMeta>();

const keyOf = (handle: unknown): string =>
  String(handle ?? '').trim().replace(/^@/, '').toLowerCase();

export function setChannelReliability(
    entries: { handle: string; reliability: string; note?: string | null }[]): void {
  byHandle = new Map(entries.map((e) => [keyOf(e.handle), {
    reliability: (e.reliability === 'high' || e.reliability === 'low'
      || e.reliability === 'standard') ? e.reliability : null,
    note: String(e.note ?? '').trim() || null,
  }]));
}

/** null = channel unknown to the config (still fine — events outlive rows). */
export function reliabilityOf(handle: unknown): Reliability | null {
  return byHandle.get(keyOf(handle))?.reliability ?? null;
}

/** The operator's treatment note for a channel, or null. */
export function treatmentOf(handle: unknown): string | null {
  return byHandle.get(keyOf(handle))?.note ?? null;
}

export async function loadChannelMeta(): Promise<void> {
  try {
    const dto = await get<{ channels:
      { handle: string; reliability: string; note?: string | null }[] }>(
      '/api/live/channel-meta');
    setChannelReliability(dto.channels);
  } catch {
    // Keep whatever we had: the annotation is advisory, and a failed load must
    // never blank the feed's claim lines.
  }
}
